// POSVendaDireta - Counter/table sale orchestrator: menu, cart, checkout, delivery
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { POSHeader } from "./POSShared";
import { POSProductSelector } from "./POSProductSelector";
import { POSCart, POSCartMobile } from "./POSCart";
import { CashChangeDialog } from "./POSPayment";
import { ModifierSelectionModal } from "./ModifierSelectionModal";
import { POSOrderComplete } from "./POSOrderComplete";
import { POSDelivererModal } from "./POSDelivererModal";
import { usePOSVendaDireta } from "./usePOSVendaDireta";
import { useCart } from "@/hooks/useCart";
import { useKeyboardAware } from "@/hooks/useKeyboardAware";
import { useWhatsAppStatus } from "@/contexts/WhatsAppStatusContext";
import { connectQZTray } from "@/utils/printService";

export function POSVendaDireta({
  authAxios,
  onBack,
  tableNumber = null,
  tableName = null,
  isTableOrder = false,
  requireCustomer = false,
  orderSource = "counter",
}) {
  const waConnected = useWhatsAppStatus();

  // Menu state
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [showMobileCart, setShowMobileCart] = useState(false);

  // Config
  const [posConfig, setPosConfig] = useState(null);
  const [restaurantData, setRestaurantData] = useState(null);

  // Modifier modal
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productModifierGroups, setProductModifierGroups] = useState([]);
  const [showModifierModal, setShowModifierModal] = useState(false);
  const [loadingModifiers, setLoadingModifiers] = useState(false);
  const modifierAbortRef = useRef(null);

  // Cart
  const {
    cart, addItem, addItemWithModifiers, updateQuantity, updateItemNotes,
    removeItem, clearCart, subtotal, total, discountAmount, setDiscountAmount,
    discountType, setDiscountType, discountValue, setDiscountValue,
    discountReason, setDiscountReason, itemCount, getCartItem, getCartTotalQty, isEmpty,
  } = useCart();

  const handleDiscountChange = useCallback(({ amount, type, value, reason }) => {
    setDiscountAmount(amount); setDiscountType(type); setDiscountValue(value); setDiscountReason(reason);
  }, [setDiscountAmount, setDiscountType, setDiscountValue, setDiscountReason]);

  const [orderNotes, setOrderNotes] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");

  // Delivery
  const [deliveryMode, setDeliveryMode] = useState(false);
  const [deliveryCustomerName, setDeliveryCustomerName] = useState("");
  const [deliveryCustomerPhone, setDeliveryCustomerPhone] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryNotes, setDeliveryNotes] = useState("");
  const [deliveryPaymentMethod, setDeliveryPaymentMethod] = useState("card");

  const handleDeliveryPhoneLookup = useCallback(async (phone) => {
    if (!phone.trim()) return;
    try {
      const res = await authAxiosRef.current.get(`/pos/customer-lookup?phone=${encodeURIComponent(phone.trim())}`);
      if (res.data?.found) {
        if (res.data.name && !deliveryCustomerName.trim()) setDeliveryCustomerName(res.data.name);
        if (res.data.address && !deliveryAddress.trim()) setDeliveryAddress(res.data.address);
      }
    } catch { /* best-effort lookup */ }
  }, [deliveryCustomerName, deliveryAddress]);

  // Language
  const [menuLang, setMenuLang] = useState(() => localStorage.getItem("kaiso_pos_lang") || "pt");
  const handleLangChange = (l) => { setMenuLang(l); localStorage.setItem("kaiso_pos_lang", l); };

  useKeyboardAware({ enabled: true, scrollOffset: 150 });

  const showPayment = !isTableOrder && !tableNumber;
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;

  useEffect(() => {
    authAxiosRef.current.get("/pos/settings").then(r => setPosConfig(r.data?.data || r.data)).catch(() => {});
    authAxiosRef.current.get("/restaurant").then(r => setRestaurantData(r.data?.data || r.data)).catch(() => {});
    const savedPrinter = localStorage.getItem("kaiso_selected_printer");
    if (savedPrinter) connectQZTray().catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMenu = useCallback(async () => {
    try {
      const res = await authAxiosRef.current.get("/pos/menu");
      setCategories(res.data.categories || []);
      setProducts(res.data.all_products || []);
    } catch { toast.error("Erro ao carregar menu"); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchMenu(); }, [fetchMenu]);

  // Handlers via hook
  const {
    orderComplete, processing, showCashDialog, setShowCashDialog,
    showDelivererModal, deliverers, assigningDeliverer,
    handleCheckout, handleCashCheckout, handleTableOrder,
    handleDeliveryCheckout, handleAssignDeliverer,
    handlePrint, handleNewOrder, closeDelivererModal,
  } = usePOSVendaDireta({
    cart, subtotal, total, discountAmount, discountType, discountValue, discountReason,
    orderNotes, setOrderNotes, clearCart, isEmpty, requireCustomer, customerName, customerPhone,
    setCustomerName, setCustomerPhone,
    orderSource, tableNumber, deliveryCustomerName, deliveryCustomerPhone,
    deliveryAddress, deliveryNotes, deliveryPaymentMethod,
    setDeliveryAddress, setDeliveryNotes, posConfig, restaurantData, waConnected, authAxios, onBack,
  });

  const handleProductClick = async (product) => {
    if (!product.modifier_group_ids?.length) { addItem(product); return; }
    if (modifierAbortRef.current) modifierAbortRef.current.abort();
    modifierAbortRef.current = new AbortController();
    setSelectedProduct(product);
    setLoadingModifiers(true);
    setShowModifierModal(true);
    try {
      const res = await authAxios.get(`/menu/products/${product.id}/modifier-groups`, { signal: modifierAbortRef.current.signal });
      setProductModifierGroups(res.data || []);
    } catch (err) {
      if (err.name === "AbortError" || err.code === "ERR_CANCELED") return;
      console.error("Error fetching modifier groups:", err);
      addItem(product);
      setShowModifierModal(false);
    }
    setLoadingModifiers(false);
  };

  const handleModifierConfirm = (data) => {
    addItemWithModifiers(data);
    setShowModifierModal(false);
    setSelectedProduct(null);
    setProductModifierGroups([]);
  };

  const extraPaymentMethods = (() => {
    const BASE = ["cash","card","dinheiro","cartão","cartao","efectivo","tarjeta"];
    const fromMethods = (restaurantData?.payment_methods || [])
      .filter(m => m.active !== false && m.name && !BASE.includes(m.name.toLowerCase()))
      .map(m => m.name);
    const legacy = restaurantData?.extra_payment_method ? [restaurantData.extra_payment_method] : [];
    return [...new Set([...fromMethods, ...legacy])].filter(Boolean);
  })();

  if (orderComplete) {
    return (
      <POSOrderComplete
        orderComplete={orderComplete}
        tableNumber={tableNumber}
        tableName={tableName}
        onPrint={handlePrint}
        onNewOrder={handleNewOrder}
        onBack={onBack}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const cartProps = {
    cart, subtotal, total, discountAmount, onDiscountChange: handleDiscountChange, itemCount,
    onUpdateQuantity: updateQuantity, onUpdateItemNotes: updateItemNotes, onRemoveItem: removeItem,
    onClearCart: clearCart, onCheckout: isTableOrder ? handleTableOrder : handleCheckout,
    showPaymentButtons: showPayment, isTableOrder, processing, orderNotes, onNotesChange: setOrderNotes,
    requireCustomer, customerName, onCustomerNameChange: setCustomerName, customerPhone,
    onCustomerPhoneChange: setCustomerPhone, extraPaymentMethods,
    deliveryMode, onDeliveryModeChange: setDeliveryMode,
    deliveryCustomerName, onDeliveryCustomerNameChange: setDeliveryCustomerName,
    deliveryCustomerPhone, onDeliveryCustomerPhoneChange: setDeliveryCustomerPhone,
    deliveryAddress, onDeliveryAddressChange: setDeliveryAddress,
    deliveryNotes, onDeliveryNotesChange: setDeliveryNotes,
    deliveryPaymentMethod, onDeliveryPaymentMethodChange: setDeliveryPaymentMethod,
    onDeliveryCheckout: handleDeliveryCheckout,
    onDeliveryPhoneLookup: handleDeliveryPhoneLookup,
  };

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      <POSHeader
        title={tableNumber
          ? `Mesa ${tableName || tableNumber}`
          : requireCustomer
            ? ({ whatsapp: "WhatsApp / Delivery", platform: "Plataforma", counter: "Balcão" }[orderSource] || "Pedido Cliente")
            : "Venda Direta"}
        onBack={onBack}
        rightContent={tableNumber && (
          <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-bold">
            MESA {tableNumber}
          </span>
        )}
      />

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 lg:w-[70%]">
          <POSProductSelector
            categories={categories}
            products={products}
            onSelectProduct={handleProductClick}
            getCartItem={getCartItem}
            getCartTotalQty={getCartTotalQty}
            loading={loading}
            lang={menuLang}
            onLangChange={handleLangChange}
            authAxios={authAxios}
            onProductsTranslated={fetchMenu}
          />
        </div>

        <div className="hidden lg:flex lg:w-[30%] max-w-sm">
          <POSCart {...cartProps} />
        </div>
      </div>

      <div className="lg:hidden">
        {!isEmpty && (
          <POSCartMobile
            cart={cart}
            subtotal={subtotal}
            itemCount={itemCount}
            isOpen={showMobileCart}
            onToggle={() => setShowMobileCart(!showMobileCart)}
          >
            <POSCart {...cartProps} />
          </POSCartMobile>
        )}
      </div>

      {showDelivererModal && (
        <POSDelivererModal
          deliverers={deliverers}
          assigningDeliverer={assigningDeliverer}
          onAssign={handleAssignDeliverer}
          onClose={closeDelivererModal}
        />
      )}

      <CashChangeDialog
        open={showCashDialog}
        onClose={() => setShowCashDialog(false)}
        total={subtotal}
        onConfirm={handleCashCheckout}
        processing={processing}
      />

      <ModifierSelectionModal
        open={showModifierModal}
        onOpenChange={(open) => {
          setShowModifierModal(open);
          if (!open) {
            if (modifierAbortRef.current) modifierAbortRef.current.abort();
            setLoadingModifiers(false);
            setTimeout(() => { setSelectedProduct(null); setProductModifierGroups([]); }, 200);
          }
        }}
        product={selectedProduct}
        modifierGroups={productModifierGroups}
        onConfirm={handleModifierConfirm}
        loading={loadingModifiers}
      />
    </div>
  );
}
